import React from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from '../Skeleton';

export default function TimeAndArtistSelection({
    selectedTime,
    setSelectedTime,
    setSelectedArtistId,
    availableTimes,
    formatTime,
    artistOptions,
    selectedArtistId,
    dateStr,
    loadingSlots,
    slots
}) {
    const { t } = useTranslation();

    if (!dateStr) return null;

    // Recommended slots (gap-filling). Times where any artist is recommended.
    const recommendedTimes = new Set(
        (slots || []).filter(s => s.recommended).map(s => s.start_time)
    );
    // Recommended artist IDs for currently-selected time
    const recommendedArtistIdsAtTime = new Set(
        (slots || [])
            .filter(s => s.recommended && s.start_time === selectedTime)
            .map(s => s.artist_id)
    );

    // Sort times: recommended first
    const sortedTimes = [...availableTimes].sort((a, b) => {
        const aR = recommendedTimes.has(a);
        const bR = recommendedTimes.has(b);
        if (aR !== bR) return aR ? -1 : 1;
        return a.localeCompare(b);
    });

    // Sort artists: recommended first
    const sortedArtistOptions = [...artistOptions].sort((a, b) => {
        const aR = recommendedArtistIdsAtTime.has(a.id);
        const bR = recommendedArtistIdsAtTime.has(b.id);
        if (aR !== bR) return aR ? -1 : 1;
        return 0;
    });

    return (
        <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.8rem', letterSpacing: '0.06em', color: '#ffffff', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.9rem', fontWeight: 700, color: 'var(--orange)', border: '2px solid var(--orange)', borderRadius: '50%', width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>3</span>
                {t('booking.selectTime')}
            </h2>

            {loadingSlots ? (
                <Skeleton count={6} height="44px" />
            ) : slots.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', padding: '1.5rem', border: '1px dashed var(--border)', borderRadius: '8px', textAlign: 'center' }}>
                    {t('booking.noSlots')}
                </p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {/* Times */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '0.75rem'
                    }}>
                        {sortedTimes.map(t24 => {
                            const isSelected = selectedTime === t24;
                            const isRecommended = recommendedTimes.has(t24);
                            return (
                                <button
                                    key={t24}
                                    onClick={() => {
                                        setSelectedTime(t24);
                                        setSelectedArtistId(null); // Reset artist when time changes
                                    }}
                                    style={{
                                        position: 'relative',
                                        padding: '0.75rem 0.5rem',
                                        background: isSelected ? 'var(--orange)' : 'var(--bg-card)',
                                        color: isSelected ? '#18181b' : '#ffffff',
                                        border: `1px solid ${isSelected ? 'var(--orange)' : isRecommended ? '#16a34a' : 'var(--border)'}`,
                                        boxShadow: isRecommended && !isSelected ? '0 0 0 1px rgba(22,163,74,0.35)' : 'none',
                                        borderRadius: '8px',
                                        fontFamily: "'Oswald', sans-serif",
                                        fontSize: '0.95rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                    }}
                                    title={isRecommended ? 'Recommended slot — fits the schedule efficiently' : undefined}
                                >
                                    {formatTime(t24)}
                                    {isRecommended && (
                                        <span style={{
                                            position: 'absolute', top: -6, right: -6,
                                            background: '#16a34a', color: '#fff',
                                            fontSize: '0.55rem', fontWeight: 800,
                                            padding: '1px 5px', borderRadius: 999,
                                            lineHeight: '1.1',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                        }}>★</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Artists */}
                    {selectedTime && artistOptions.length > 0 && (
                        <div style={{ marginTop: '1rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)' }}>
                            <h3 style={{ fontSize: '1rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>
                                {t('booking.selectArtist')}
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                                {sortedArtistOptions.map(art => {
                                    const isSelected = selectedArtistId === art.id;
                                    const isRecommended = recommendedArtistIdsAtTime.has(art.id);
                                    return (
                                        <div
                                            key={art.id}
                                            role="button"
                                            tabIndex={0}
                                            aria-pressed={isSelected}
                                            onClick={() => setSelectedArtistId(art.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setSelectedArtistId(art.id);
                                                }
                                            }}
                                            style={{
                                                position: 'relative',
                                                padding: '1rem',
                                                background: isSelected ? 'var(--card-focus-ring)' : 'var(--bg-card)',
                                                border: `1px solid ${isSelected ? 'var(--orange)' : isRecommended ? '#16a34a' : 'var(--border)'}`,
                                                boxShadow: isRecommended && !isSelected ? '0 0 0 1px rgba(22,163,74,0.30)' : 'none',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '1rem',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {isRecommended && !isSelected && (
                                                <span style={{
                                                    position: 'absolute', top: 8, right: 8,
                                                    background: '#16a34a', color: '#fff',
                                                    fontSize: '0.6rem', fontWeight: 800,
                                                    padding: '2px 7px', borderRadius: 999,
                                                    letterSpacing: '0.04em',
                                                }}>★ RECOMMENDED</span>
                                            )}
                                            <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: 'var(--bg-dark)', overflow: 'hidden', flexShrink: 0 }}>
                                                {art.photo ? (
                                                    <img src={art.photo} alt={art.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'var(--text-muted)' }}>👤</div>
                                                )}
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#fff', marginBottom: '0.2rem' }}>{art.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--orange)', textTransform: 'uppercase' }}>{art.role}</div>

                                                {art.rating > 0 && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.3rem' }}>
                                                        <span style={{ color: '#FFD700', fontSize: '0.8rem' }}>★</span>
                                                        <span style={{ fontSize: '0.75rem', color: '#fff', fontWeight: 600 }}>{art.rating}</span>
                                                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>({art.rating_count})</span>
                                                    </div>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <div style={{ marginLeft: 'auto', color: 'var(--orange)' }}>
                                                    ✓
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
